import React from 'react';
import clsx from 'clsx';

import { Checkbox, Link as PostmasterLink, Text } from '@auspost/postmaster-react';

import MerchantLogo from 'App/common/MerchantLogo/MerchantLogo';
import ConsignmentIcon from 'App/DetailsPage/SummaryPanel/Nickname/ConsignmentIcon/ConsignmentIcon';
import CheckIcon from 'assets/green-check-icon.svg';
import { Merchant, Milestone, MilestoneLegacy, Shipment, TimeWindow } from 'models/shipments';
import { createEddTagInfo } from 'utils/edd.utils';

import {
  SHIPMENT_LIST_EDD_TEST_ID,
  SHIPMENT_LIST_ENQUIRY_BLANK_TEST_ID,
  SHIPMENT_LIST_ENQUIRY_LINK_TEST_ID,
  SHIPMENT_LIST_ENQUIRY_TEST_ID,
  SHIPMENT_LIST_FLIGHT_STATUS_TEST_ID,
  SHIPMENT_LIST_ITEM_TEST_ID,
  SHIPMENT_LIST_MILESTONE_TEST_ID,
  SHIPMENT_LIST_TRACK_IDENTIFIER_TEST_ID,
} from '../ShipmentList.test-ids';

import styles from './ShipmentListItem.module.scss';

const RouterLink = React.lazy(() => import('react-router-dom').then((reactRouter) => ({ default: reactRouter.Link })));

/**
 * A minimal model of shipment information used by `<ShipmentListItem>`
 */
export interface ShipmentProp {
  merchant?: Merchant;
  articles: {
    articleId: string;
    sender: string | null;
    nickname: string | null;
    events: { milestone: string; dateTime: number }[] | null;
    milestones: { name: string }[] | null;
    trackStatusOfArticle: string;
    dateRevised: boolean;
    timeWindow: TimeWindow | null;
    estimatedDeliveryDateRange: {
      fromDate: string;
      toDate: string;
    } | null;
    deliveredByDateISO?: string | null;
  }[];
  consignmentId: string;
  articleCount: number;
  milestone?: MilestoneLegacy | null;
}

export interface ShipmentEnquiry {
  enquiries: string[];
  helpAndSupportBaseUrl: string;
  onEnquiryClick?: (
    e: React.MouseEvent<HTMLAnchorElement>,
    caseIds?: string[],
    shipment?: Shipment | ShipmentProp
  ) => void;
}

interface ShipmentListItemProps extends React.HTMLAttributes<HTMLAnchorElement> {
  shipment: Shipment | ShipmentProp;
  editMode?: boolean;
  isSelected?: boolean; // determines the display of the checkbox
  onItemSelectChange?: (event: React.ChangeEvent<HTMLInputElement>, shipment: Shipment | ShipmentProp) => void;
  shipmentEnquiry?: ShipmentEnquiry;
  config?: ShipmentListItemConfig;
}

const ShipmentListItem: React.FC<ShipmentListItemProps> = ({
  shipment,
  shipmentEnquiry,
  config,
  isSelected,
  onClick,
  onItemSelectChange,
  editMode = false,
  ...props
}) => {
  const [showEditMode, setShowEditMode] = React.useState(false);
  const merchant = shipment.merchant;
  const merchantLogo = merchant?.logoUrl ?? '';
  const merchantName = merchant?.displayName ?? null;
  const firstItem = shipment.articles?.length ? shipment.articles[0] : null;

  const showEnquiriesColumn = !!shipmentEnquiry;

  if (!firstItem) return null;
  const { articleId, sender, nickname } = firstItem;
  const senderName = merchantName ?? sender;
  const latestMilestone =
    shipment.milestone?.name ??
    firstItem?.events?.[0]?.milestone ??
    firstItem?.milestones?.[0]?.name ??
    firstItem?.trackStatusOfArticle;
  const isConsignment = (shipment.articleCount ?? 0) > 1 || shipment.articles?.length > 1;
  const trackingNumber = isConsignment ? shipment.consignmentId : articleId;
  const edd = createEddTagInfo(firstItem);

  React.useEffect(() => {
    // Stagger editMode and showEditMode, to allow for transitions before rerender
    let timeout: NodeJS.Timeout;
    if (editMode && !showEditMode) {
      setShowEditMode(true);
    }
    if (!editMode && showEditMode) {
      timeout = setTimeout(() => {
        setShowEditMode(false);
      }, 300);
    }
    return () => {
      clearTimeout(timeout);
    };
  }, [editMode]);

  let mainIdentifier, subIdentifier;

  if (nickname && senderName) {
    mainIdentifier = nickname;
    subIdentifier = `From ${senderName}`;
  } else if (nickname || senderName) {
    mainIdentifier = nickname ?? `From ${senderName}`;
    subIdentifier = trackingNumber;
  } else {
    mainIdentifier = trackingNumber;
  }

  function generateExpectedDeliveryInfo() {
    if (!edd) {
      if (latestMilestone === Milestone.COMING_TODAY) return 'Expected today';
      return null;
    }
    const { estimatedDeliveryPrefix = '', estimatedDeliveryText } = edd;
    const eddText = `${estimatedDeliveryPrefix || 'Expected today'} ${estimatedDeliveryText}`;
    return eddText;
  }

  const eddInfo = generateExpectedDeliveryInfo();

  const renderEnquiryLink = () => {
    if (!shipmentEnquiry) return;
    const { enquiries, onEnquiryClick, helpAndSupportBaseUrl } = shipmentEnquiry;
    if (!enquiries?.length) return <span data-testid={SHIPMENT_LIST_ENQUIRY_BLANK_TEST_ID} />;
    const onEnquiryClickWrapper = (e: React.MouseEvent<HTMLAnchorElement>) => {
      // Due to use of nested links, we want to stop the outer links onClick from triggering
      e.stopPropagation();
      if (onEnquiryClick) onEnquiryClick(e, enquiries, shipment);
    };
    let enquiryUrl = helpAndSupportBaseUrl;
    if (enquiries.length === 1) enquiryUrl += `/case/${enquiries[0]}/`;

    return (
      <PostmasterLink
        variant='text'
        href={enquiryUrl}
        target='_blank'
        onClick={onEnquiryClickWrapper}
        data-testid={SHIPMENT_LIST_ENQUIRY_LINK_TEST_ID}
      >
        Enquiry raised
      </PostmasterLink>
    );
  };

  const linkProps = {
    className: clsx(styles.listItemWrapper, {
      [styles.withEnquiry]: showEnquiriesColumn,
      [styles.editMode]: editMode,
      [styles.slideOut]: showEditMode && !editMode,
    }),
    'data-testid': SHIPMENT_LIST_ITEM_TEST_ID,
    onClick: editMode ? null : onClick, // Edit mode clicks should be handled by onItemSelectChange
    ...props,
  };

  return (
    <LinkWrapper trackingIdentifier={articleId} linkProps={linkProps} config={config} editMode={showEditMode}>
      <Checkbox
        type='checkbox'
        isChecked={isSelected}
        onChange={(event) => onItemSelectChange && onItemSelectChange(event, shipment)}
        id={'select-remove-checkbox-' + articleId}
        labelledBy=''
      />

      <MerchantLogo className={styles.merchant} logoSrc={merchantLogo} />
      <div className={styles.trackingIdentifier} data-testid={SHIPMENT_LIST_TRACK_IDENTIFIER_TEST_ID}>
        <div className={styles.main}>
          <Text variant='body-large' weight='bold'>
            {mainIdentifier}
          </Text>
          {isConsignment && (
            <ConsignmentIcon
              size={shipment.articleCount ?? shipment.articles?.length ?? 0}
              className={styles.consignmentIcon}
            />
          )}
        </div>
        {subIdentifier && (
          <Text variant='body-small' className={styles.sub}>
            {subIdentifier}
          </Text>
        )}
      </div>
      <Text variant='body-small' className={styles.enquiryLink} data-testid={SHIPMENT_LIST_ENQUIRY_TEST_ID}>
        {renderEnquiryLink()}
      </Text>
      <Text variant='body-small' className={styles.milestone} data-testid={SHIPMENT_LIST_MILESTONE_TEST_ID}>
        {latestMilestone}
        {latestMilestone === Milestone.DELIVERED && <CheckIcon />}
      </Text>

      {eddInfo && (
        <div className={styles.edd}>
          <Text variant='body-small' data-testid={SHIPMENT_LIST_EDD_TEST_ID}>
            {eddInfo}
          </Text>
        </div>
      )}

      {edd?.flightStatus && (
        <Text
          variant='body-small'
          className={clsx(styles.flightStatus, styles[edd.cssClassName])}
          data-testid={SHIPMENT_LIST_FLIGHT_STATUS_TEST_ID}
        >
          {edd.flightStatus}
        </Text>
      )}
    </LinkWrapper>
  );
};

export interface ShipmentListItemConfig {
  routerLink?: boolean;
  trackDetailPath?: string;
}
interface LinkWrapperProps {
  trackingIdentifier: string;
  children: React.ReactNode;
  linkProps: Record<string, unknown>;
  editMode?: boolean;
  config?: ShipmentListItemConfig;
}
const LinkWrapper: React.FC<LinkWrapperProps> = ({
  trackingIdentifier,
  children,
  linkProps,
  editMode = false,
  config,
}) => {
  if (editMode) return <label {...linkProps}>{children}</label>;

  const routerLink = config?.routerLink === true;
  const trackDetailsPath = config?.trackDetailPath || 'https://auspost.com.au/mypost/track/details/';
  const defaultLink = (
    <a {...linkProps} href={trackDetailsPath + trackingIdentifier}>
      {children}
    </a>
  );
  if (!routerLink) return defaultLink;

  return (
    <React.Suspense fallback={defaultLink}>
      <RouterLink {...linkProps} to={'../details/' + trackingIdentifier}>
        {children}
      </RouterLink>
    </React.Suspense>
  );
};

export default ShipmentListItem;
